import Button from "@/components/Button";
import IconButton from "@/components/IconButton";
import Menu, { type MenuItem } from "@/components/Menu";
import type { TableAction } from "@/components/Table";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { createContext, useContext, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";

type IndexTabBarActionsContextValue = {
  actions: TableAction[];
  setActions: (actions: TableAction[]) => void;
};

const IndexTabBarActionsContext = createContext<IndexTabBarActionsContextValue | null>(null);

export function IndexTabBarActionsProvider({ children }: { children: ReactNode }) {
  const [actions, setActions] = useState<TableAction[]>([]);
  const value = useMemo(() => ({ actions, setActions }), [actions]);

  return <IndexTabBarActionsContext.Provider value={value}>{children}</IndexTabBarActionsContext.Provider>;
}

function actionSignature(actions: TableAction[]) {
  return actions
    .map((action) => [action.label, action.testId, action.variant, action.disabled, Boolean(action.render)].join(":"))
    .join("|");
}

export function useSetIndexTabBarActions(actions: TableAction[]) {
  const ctx = useContext(IndexTabBarActionsContext);
  const setActions = ctx?.setActions;
  const actionsRef = useRef(actions);
  actionsRef.current = actions;
  const signature = actionSignature(actions);

  useLayoutEffect(() => {
    if (!setActions) return;

    const proxied = actionsRef.current.map((action, index) => ({
      ...action,
      onClick: () => {
        actionsRef.current[index]?.onClick();
      },
      render: action.render
        ? () => {
            return actionsRef.current[index]?.render?.() ?? null;
          }
        : undefined
    }));

    setActions(proxied);
    return () => setActions([]);
  }, [setActions, signature]);
}

export function IndexTabBarActions({ actions }: { actions: TableAction[] }) {
  useSetIndexTabBarActions(actions);
  return null;
}

export function TabBarActions({ actions }: { actions: TableAction[] }) {
  const { isCompactLayout } = useIsCompactLayout();
  if (actions.length === 0) return null;

  if (isCompactLayout) {
    const [firstAction, ...overflowActions] = actions;
    return (
      <div className="flex items-center gap-2" data-testid="index-tab-bar-actions">
        {firstAction.render ? (
          <div>{firstAction.render()}</div>
        ) : (
          <Button
            variant={firstAction.variant || "filled"}
            icon={firstAction.icon}
            onClick={firstAction.onClick}
            disabled={firstAction.disabled}
            testId={firstAction.testId || "table-action-0"}
          >
            {firstAction.label}
          </Button>
        )}
        {overflowActions.length > 0 ? (
          <Menu
            title="More actions"
            AnchorComponent={
              <IconButton
                name="pending"
                data-testid="index-tab-bar-actions-menu-button"
                ariaLabel="More actions"
                skipMargin
              />
            }
            menuItems={overflowActions.map(
              (action) =>
                ({
                  label: action.label,
                  onClick: action.onClick,
                  ...(action.icon ? { iconName: action.icon } : {}),
                  dataTestId: action.testId
                }) as MenuItem
            )}
          />
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3" data-testid="index-tab-bar-actions">
      {[...actions].reverse().map((action, index) => {
        const originalIndex = actions.length - 1 - index;
        return action.render ? (
          <div key={originalIndex}>{action.render()}</div>
        ) : (
          <Button
            key={originalIndex}
            variant={action.variant || "filled"}
            icon={action.icon}
            onClick={action.onClick}
            disabled={action.disabled}
            testId={action.testId || `table-action-${originalIndex}`}
          >
            {action.label}
          </Button>
        );
      })}
    </div>
  );
}

export function useIndexTabBarActionIcon(): ReactNode {
  const ctx = useContext(IndexTabBarActionsContext);
  const actions = ctx?.actions ?? [];
  if (actions.length === 0) return null;
  return <TabBarActions actions={actions} />;
}
